import { useMemo } from 'react';
import {
  Box,
  Card,
  CardSection,
  EmptyState,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  Flex,
  LabelIcon,
  Link,
  Text,
  SkeletonShape,
} from '@customerio/pluma-components/react';
import { Chart } from '@/components/highcharts';
import { generatePath } from '@/services/router/generate-path';
import { useServicesQuery } from '@/services/api';
import { SectionLabel } from './section-label';
import { aggregateMetrics, normalizeTimezone } from './activity-chart-utils';

function analysisHref(workspaceId: number): string {
  const now = new Date();
  const start = new Date(now.getTime() - 30 * 86400 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const params = new URLSearchParams({
    channels: 'email_twilio_webhook_slack_in_app',
    data_types: 'campaign_newsletter_broadcast',
    start_time: fmt(start),
    end_time: fmt(now),
    measures:
      'delivery_metrics.sent-delivery_metrics.delivered-delivery_metrics.opened-delivery_metrics.converted-delivery_metrics.unsubscribed',
  });
  return generatePath('/workspaces/:workspaceId/journeys/analysis', { workspaceId }) + '?' + params.toString();
}

// NOTE: Do not call loadHighcharts() at module scope. The Chart component
// handles async loading internally and shows a placeholder until ready.

interface ActivityChartProps {
  workspaceId: number;
}

function useWorkspaceTimezone(workspaceId: number) {
  const { data, isSuccess } = useServicesQuery(
    'getWorkspaceSettings',
    { params: { environment_id: workspaceId } },
    { staleTime: 300_000, enabled: workspaceId > 0 },
  );
  const tz = data?.settings?.metrics_timezone;
  const resolved =
    tz != null && tz.length > 0 ? normalizeTimezone(tz) : Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { tz: resolved, isReady: isSuccess };
}

function useDeliveryMetrics(workspaceId: number) {
  const { tz, isReady } = useWorkspaceTimezone(workspaceId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Stabilize timestamp so the query key doesn't change on every render
  const now = useMemo(() => Math.floor(Date.now() / 1000), []);
  const days = 30;
  const start = now - days * 86400;

  const query = useServicesQuery(
    'getAllDeliveries',
    {
      params: { environment_id: workspaceId },
      query: { version: '2', res: 'days', start, end: now, tz },
    },
    { staleTime: 300_000, enabled: workspaceId > 0 && isReady },
  );
  return { ...query, tz };
}

export function ActivityChart({ workspaceId }: ActivityChartProps) {
  const { data, isPending, tz } = useDeliveryMetrics(workspaceId);

  const { sent, delivered, opened, categories } = useMemo(() => aggregateMetrics(data?.metrics ?? [], tz), [data, tz]);

  const hasData = sent.some((v) => v > 0);

  const chartOptions = useMemo(
    () => ({
      chart: { type: 'areaspline' as const, height: 220, spacing: [8, 16, 8, 16] },
      xAxis: { categories, labels: { step: 5 } },
      yAxis: { title: { text: undefined } },
      tooltip: { shared: true },
      legend: { align: 'left' as const, verticalAlign: 'top' as const },
      plotOptions: {
        areaspline: { fillOpacity: 0.08, lineWidth: 2, marker: { enabled: false } },
      },
      series: [
        { name: 'Sent', data: sent },
        { name: 'Delivered', data: delivered },
        { name: 'Opened', data: opened },
      ],
    }),
    [sent, delivered, opened, categories],
  );

  return (
    <Box w="full">
      <Flex alignItems="center" justifyContent="space-between" mb="150">
        <SectionLabel>Workspace activity</SectionLabel>
        <Text size="xs">
          <Link href={analysisHref(workspaceId)} variant="primary" icon="chevron-right" iconPosition="trailing">
            More analysis
          </Link>
        </Text>
      </Flex>
      {isPending ? (
        <Card style={{ border: 'none' }}>
          <CardSection>
            <SkeletonShape style={{ width: '100%', height: 220 }} />
          </CardSection>
        </Card>
      ) : !hasData ? (
        <Box py="300">
          <EmptyState>
            <EmptyStateGraphic>
              <LabelIcon name="analysis" color="grey" size="lg" />
            </EmptyStateGraphic>
            <EmptyStateTitle>No sends in the last 30 days</EmptyStateTitle>
            <EmptyStateDescription>
              Sends and deliveries will show up here once messages are sent.
            </EmptyStateDescription>
          </EmptyState>
        </Box>
      ) : (
        <Chart options={chartOptions} />
      )}
    </Box>
  );
}
